import { BenchmarkType } from '@/api/rest/resources/types/fact';
import { isNullish } from '@/utils/booleans';

/**
 * These utilities are for asserting conditions inside source code. Do not use
 * this to write assertions inside test files..
 */

/**
 * **Do not use for writing tests.**
 *
 * Assert a condition. If condition is not met, an error is thrown with the
 * provided message. It also acts as a type-guard.
 *
 * @example
 * ```ts
 * const user: { email?: string } = {};
 *
 * assert(
 *   typeof user.email === "string" && user.email.length > 0,
 *   `Expected valid email, received ${user.email}`,
 * );
 *
 * const email = user.email; // email is of type string
 * ```
 */
export const assert: (condition: unknown, message: string) => asserts condition = (condition, message) => {
  if (!condition) {
    throw Error(message);
  }
};

/**
 * **Do not use for writing tests.**
 *
 * Assert whether something is not null or undefined, throws an error otherwise.
 * It also acts as a type-guard.
 *
 * @example
 * ```ts
 * const user: { email?: string | null } = {}
 *
 * assertIsDefined(obj.email);
 *
 * const email = user.email // email is of type string
 * ```
 */
export const assertIsDefined: <T>(value: T, name?: string) => asserts value is NonNullable<T> = (val, name) => {
  if (isNullish(val)) {
    throw new Error(`Expected ${name ?? 'value'} to be defined, but received ${val}`);
  }
};

/**
 * **Do not use for writing tests.**
 *
 * Assert the type of a benchmark. If the received benchmark matches benchmarkCheck, an error is thrown.
 *
 * @param benchmark - The BenchmarkType type to check
 * @param source - A string describing the source of the benchmark, to be used in the error message
 * @param benchmarkCheck - The benchmark type that is not supported. Defaults to BenchmarkType.very_low
 */
export const assertBenchmarkType: {
  (
    benchmark: BenchmarkType | null | undefined,
    source: string,
  ): asserts benchmark is Exclude<BenchmarkType, BenchmarkType.very_low>;
  <T extends BenchmarkType>(
    benchmark: BenchmarkType | null | undefined,
    source: string,
    benchmarkCheck: T,
  ): asserts benchmark is Exclude<BenchmarkType, T>;
} = (
  benchmark: BenchmarkType | null | undefined,
  source: string,
  benchmarkCheck: BenchmarkType = BenchmarkType.very_low,
) => {
  if (benchmark === benchmarkCheck) {
    throw new Error(`Benchmark type ${benchmarkCheck} is not supported for ${source}`);
  }
};
